import { ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";
import { APPROVAL_GATES } from "./approval-types";
import { PeriodApprovalPanel } from "./period-approval-panel";
import type { ApprovalChangeHandler, PendingApproval } from "./types";
import { getApprovalCoverage } from "./utils";

interface ApprovalCoverageControlProps {
  approvalStatus?: IPayrollSheetApprovalStatus | null;
  pendingApproval?: PendingApproval | null;
  onApprovalChange?: ApprovalChangeHandler;
}

/**
 * Period-wide sign-off, sitting on the same line as "calculated N minutes ago"
 * rather than in its own bordered box — it is standing context about the period,
 * not a call to action competing with the header's buttons. Two counts because
 * the two gates answer different questions ("can PayPacks go out?" and "is payroll
 * done?"), and a single combined number would answer neither.
 */
export const ApprovalCoverageControl = ({
  approvalStatus,
  pendingApproval,
  onApprovalChange,
}: ApprovalCoverageControlProps) => {
  const coverage = getApprovalCoverage(approvalStatus);
  if (!approvalStatus || coverage.total === 0) return null;

  // "Approvals 2/9 1/9" is unreadable out loud, so the button carries the spelt
  // out version and the counts stay a visual shorthand.
  const summary = APPROVAL_GATES.map(
    (gate) =>
      `${gate.label}: ${coverage.approvedByType[gate.type]} of ${coverage.total}`,
  ).join(", ");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Approvals. ${summary}`}
          // -my-1 py-1.5 keeps the row exactly where it was while giving the
          // control a ~26px hit area. At py-0.5 it was an 18px strip of text:
          // a few pixels of vertical mouse movement left the button, so the
          // pointer flickered back to an arrow and the thing felt broken.
          className={cn(
            "group -mx-1.5 -my-1 flex w-fit cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-1.5",
            "text-secondary-text transition-colors hover:bg-surface-secondary hover:text-primary-text",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <Typography variant="bodyW3" className="text-sm">
            Approvals
          </Typography>
          {APPROVAL_GATES.map(({ type, Icon }) => {
            const approved = coverage.approvedByType[type];
            const isComplete = approved === coverage.total;

            return (
              <Stack key={type} direction="row" align="center" gap="xs">
                <Icon
                  aria-hidden="true"
                  className={cn("h-3.5 w-3.5", {
                    "text-primary": isComplete,
                    "text-tertiary-text": !isComplete,
                  })}
                />
                <Typography
                  variant="captionW1SemiBold"
                  className={cn({ "text-primary": isComplete })}
                >
                  {approved}/{coverage.total}
                </Typography>
              </Stack>
            );
          })}
          {/* The one thing that says "this opens something". Everything else
                  on this line is static context — "Calculated 5 days ago" sits
                  immediately beside it in the same size and colour — so without a
                  disclosure mark the control reads as a label and goes unclicked. */}
          <ChevronDown
            aria-hidden="true"
            className="h-3.5 w-3.5 text-tertiary-text transition-transform duration-200 group-data-[state=open]:rotate-180"
          />
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-[28rem] p-0">
        <PeriodApprovalPanel
          accessKeys={approvalStatus.access_keys}
          pendingApproval={pendingApproval}
          onApprovalChange={onApprovalChange}
        />
      </PopoverContent>
    </Popover>
  );
};
